/**
 * PublicNcr — anonymous page that lets an external party read an NCR
 * raised against one of their bookings and post a comment / resolution.
 *
 * Route: /p/ncr/:token  (no auth required)
 *
 * Backed by two SECURITY DEFINER RPCs from 041_ncr_external_workflow.sql:
 *   - lbs_get_ncr_public(token)
 *   - lbs_submit_ncr_external_comment(token, body, name, email)
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Send, Truck } from "lucide-react";
import { getPublicNcr, submitPublicNcrComment } from "@/lib/api";
import { toast } from "sonner";
import { useTenantBranding, TenantLogo } from "@/components/TenantBrand";

export default function PublicNcr() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const brand = useTenantBranding({ ncr_token: token });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    const { data, error } = await getPublicNcr(token);
    if (error) {
      setState({ loading: false, data: null, error: error.message });
      return;
    }
    if (data?.error) {
      setState({ loading: false, data: null, error: data.error });
      return;
    }
    setState({ loading: false, data, error: null });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getPublicNcr(token);
      if (cancelled) return;
      if (error) { setState({ loading: false, data: null, error: error.message }); return; }
      if (data?.error) { setState({ loading: false, data: null, error: data.error }); return; }
      setState({ loading: false, data, error: null });
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!body.trim() || !name.trim()) {
      toast.error("Please supply your name and a comment.");
      return;
    }
    setSubmitting(true);
    const { data, error } = await submitPublicNcrComment(token, body, name, email);
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    if (data?.error) { toast.error(data.error); return; }
    toast.success("Comment posted");
    setBody("");
    load();
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading NCR…</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full" data-testid="public-ncr-error">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" /> Link invalid or expired
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600">
            <p>{state.error}</p>
            <p className="mt-2">If you believe this link should still work, please contact the site that issued it.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { issue, booking, comments } = state.data;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-5" data-testid="public-ncr-page">
        {/* Brand */}
        <TenantLogo brand={brand} />
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="rounded-md bg-blue-600 p-1"><Truck className="h-3 w-3 text-white" /></div>
          timeslot.au · Non-Conformance Report
        </div>

        {/* NCR details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-start justify-between gap-3">
              <span>{issue.title || "Non-Conformance Report"}</span>
              <span className={`text-xs px-2 py-1 rounded ${issue.status === "closed" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {issue.status}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Booking">{booking?.reference_number || "—"}</Field>
              <Field label="Site">{booking?.site_name || "—"}</Field>
              <Field label="Scheduled">{booking?.scheduled_start ? new Date(booking.scheduled_start).toLocaleString("en-AU", { hourCycle: "h23" }) : "—"}</Field>
              <Field label="Direction">{booking?.direction || "—"}</Field>
              <Field label="Sender">{booking?.sender_company || "—"}</Field>
              <Field label="Receiver">{booking?.receiver_company || "—"}</Field>
              {booking?.external_ref && <Field label="Client ref">{booking.external_ref}</Field>}
              {booking?.internal_ref && <Field label="Internal ref">{booking.internal_ref}</Field>}
            </div>
            <div className="border-t pt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Type</div>
              <div className="font-medium">{issue.issue_type}</div>
            </div>
            {issue.body && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Description</div>
                <div className="whitespace-pre-wrap">{issue.body}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comments thread */}
        <Card>
          <CardHeader><CardTitle className="text-base">Comments &amp; resolution</CardTitle></CardHeader>
          <CardContent>
            {(comments || []).length === 0 ? (
              <div className="text-sm text-slate-400 italic">No comments yet — be the first to respond.</div>
            ) : (
              <ul className="space-y-3">
                {comments.map((c) => (
                  <li
                    key={c.id}
                    className={`rounded-md border p-3 ${c.author_kind === "external" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}
                    data-testid={`ncr-comment-${c.id}`}
                  >
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span><strong className="text-slate-800">{c.author_name}</strong> · {c.author_kind}</span>
                      <span>{new Date(c.created_at).toLocaleString("en-AU", { hourCycle: "h23" })}</span>
                    </div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{c.body}</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Add comment form */}
        {issue.status !== "closed_no_replies" && (
          <Card>
            <CardHeader><CardTitle className="text-base">Add your response</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Your name *</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-ncr-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Your email (optional)</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="public-ncr-email" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Comment / resolution *</label>
                <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="public-ncr-body"
                  placeholder="Describe the corrective action you have taken or any further information." />
              </div>
              <div className="flex justify-end">
                <Button onClick={submit} disabled={submitting} data-testid="public-ncr-submit">
                  <Send className="mr-2 h-4 w-4" /> {submitting ? "Sending…" : "Post comment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}
